import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Inject, Injectable, Optional, PLATFORM_ID, TransferState, DOCUMENT } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { ORIGIN, PATH } from '../tokens';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HandledHttpResponse } from '@uzenith360/http-utils';
import orderDataKey from './fetch-data-from-db/order-data.key';
import Order from './fetch-data-from-db/types/order.interface';

@Injectable({
  providedIn: 'root'
})
export class OrderResolver {
  constructor(
    @Inject(PATH) @Optional() private readonly path: string,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(ORIGIN) @Optional() private readonly origin: string,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly transferState: TransferState,
  ) { }

  private static fetchDataFromDb?: (origin: string, code: string) =>
    Promise<HandledHttpResponse<Order>>;

  private async getFetchOrderFromDb(): Promise<
    (origin: string, code: string) =>
      Promise<HandledHttpResponse<Order>>
  > {
    if (OrderResolver.fetchDataFromDb !== undefined) {
      return OrderResolver.fetchDataFromDb;
    }

    const { fetchOrder } = await import('./fetch-data-from-db');

    return OrderResolver.fetchDataFromDb = fetchOrder;
  }

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<{ data?: Order, message?: string | undefined, statusCode: number }> {
    const { message, statusCode, data } = await firstValueFrom(
      await this.fetchOrder(this.origin ?? this.document.location.origin, this.path ?? this.document.location.pathname)
    );

    if (statusCode === 200 && !!data) {
      return {
        statusCode,
        data,
      };
    } else {
      return { message, statusCode }
    }
  }

  private async fetchOrder(origin: string, path: string): Promise<Observable<HandledHttpResponse<Order>>> {
    const defaultErrorResponse = { message: 'Problem loading order, please try again', statusCode: 500, data: undefined };

    if (isPlatformServer(this.platformId)) {
      const result: HandledHttpResponse<Order>
        = await (await this.getFetchOrderFromDb())(origin, path.substring(path.lastIndexOf('/') + 1))
          .catch(
            (err) => {
              console.error(err);

              return defaultErrorResponse;
            }
          );

      this.transferState.set(orderDataKey, result);

      return of(result);
    } else if (isPlatformBrowser(this.platformId)) {
      return of(this.transferState.get(orderDataKey, defaultErrorResponse));
    } else {
      console.error('Unexpected platform');

      return of(defaultErrorResponse);
    }
  }
}
